import { useContext, useEffect, useState } from 'react'
import { withTranslation } from 'react-i18next';
import { BellAlertIcon, ClockIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { CompletionsContext, ContactIdContext, LoadingContext } from '../../../App';
import { MsalInstanceContext, VariablesContext } from '../../../auth/AppProvider';
import { initCompletion } from '../../../utils/requests/completions';
import LoadingSpinner from '../../../components/loading/LoadingSpinner';
import * as Icons from '@heroicons/react/24/outline';

function NewFormCard({ t, completion, type, filters, featured }) {
    const [countdown, setCountdown] = useState({ days: '00', hours: '00', minutes: '00' });
    let navigate = useNavigate();
    const [completions, setCompletions] = useContext(CompletionsContext);
    const msalInstance = useContext(MsalInstanceContext);
    const variables = useContext(VariablesContext);
    const [loading, setLoading] = useContext(LoadingContext);
    const contactId = useContext(ContactIdContext);
    const [filterValues, setFilterValues] = useState([]);

    useEffect(() => {
        if (filters?.length > 0) {
            const vals = filters.map(f => {
                let value = completion.properties?.hasOwnProperty(f.name) ? completion.properties[f.name] : null;
                const option = f.options?.find(o => o.value === value);
                if (option) value = option.label;
                return { ...f, value };
            }).filter(f => f.value !== null && f.value !== "" && f.value !== undefined);
            setFilterValues(vals);
        }
        if (completion.activationDateTime?.endDate) {
            const t2 = Date.parse(completion.activationDateTime.endDate) - Date.now();
            setCountdown({
                days: Math.floor(t2 / 1000 / 60 / 60 / 24),
                hours: Math.floor((t2 / 1000 / 60 / 60) % 24),
                minutes: Math.floor((t2 / 1000 / 60) % 60),
            });
        }
    }, [filters, completion]);

    function postulate() {
        if (!loading) {
            setLoading(true);
            initCompletion(msalInstance, variables, contactId, completion).then(res => {
                if (res.ok) return res.json();
            }).then(newCandidature => {
                if (newCandidature) {
                    var completionsLoc = Object.assign([], completions);
                    var index = completionsLoc.findIndex(c => c.id === completion.id);
                    completionsLoc[index] = newCandidature;
                    setCompletions(completionsLoc);
                    setLoading(false);
                    localStorage.setItem("lastCandidature", JSON.stringify(newCandidature.objectAssociated));
                    navigate("/" + type.name + "/" + completion.id);
                }
            }).catch(() => setLoading(false));
        }
    }

    const mainBadge = filterValues[0];
    const levelBadge = filterValues[1];
    const metaValues = filterValues.slice(2, 5);
    const description = completion.parameters?.find(p => p.name === "description")?.value
        ?? completion.properties?.description;
    const prix = completion.parameters?.find(p => p.name === "prix")?.value
        ?? completion.properties?.prix
        ?? completion.properties?.tarif;


    const isUrgent = completion.activationDateTime?.endDate && countdown.days < 7;

    return (
        <div
            className="bg-white border border-gray-100 p-5 flex flex-col justify-between gap-3 cursor-pointer group"
            style={{
                minHeight: featured ? "200px" : "180px",
                borderRadius: '20px',
                borderColor: '#eceef2',
                boxShadow: 'rgba(16, 20, 34, 0.15) 0px 12px 30px -20px',
                transition: 'box-shadow 0.25s ease, transform 0.25s ease, border-color 0.25s ease',
            }}
            onMouseEnter={e => {
                e.currentTarget.style.boxShadow = 'rgba(16, 20, 34, 0.4) 0px 22px 44px -30px';
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.borderColor = 'color-mix(in srgb, var(--primary-color) 30%, #ECEEF2)';
            }}
            onMouseLeave={e => {
                e.currentTarget.style.boxShadow = 'rgba(16, 20, 34, 0.15) 0px 12px 30px -20px';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = '#eceef2';
            }}
        >
            {/* Urgency banner */}
            {isUrgent && (
                <div className="text-xs font-bold text-red-500 flex items-center gap-1">
                    <BellAlertIcon className="h-4 w-4 tilt-shaking" />
                    {countdown.days > 0
                        ? (countdown.days > 1 ? t("Only {{count}} days left", { count: countdown.days }) : t("Only {{count}} day left", { count: countdown.days }))
                        : countdown.hours > 0
                            ? t("Only {{count}} hour left", { count: countdown.hours })
                            : t("Only {{count}} min left", { count: countdown.minutes })}
                </div>
            )}

            {/* Top badges row */}
            <div className="flex items-center justify-between gap-2">
                {mainBadge ? (
                    <span className="text-xs font-bold color-primary-perso tracking-widest uppercase">
                        {mainBadge.value}
                    </span>
                ) : <span />}
                {levelBadge ? (
                    <span className="text-xs text-gray-500 font-medium bg-gray-100 px-2 py-0.5 rounded-full">
                        {levelBadge.value}
                    </span>
                ) : null}
            </div>

            {/* Title */}
            <div>
                <h2 className={`font-bold text-gray-900 leading-snug ${featured ? "text-xl" : "text-base"}`} lang="fr" translate="yes">
                    {completion.label}
                </h2>
                {description && (
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2 leading-relaxed">{description}</p>
                )}
            </div>

            {/* Meta info from remaining filters */}
            {metaValues.length > 0 && (
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                    {metaValues.map((f, i) => {
                        const Icon = Icons[f.icon] ?? ClockIcon;
                        return (
                            <span key={f.id} className="flex items-center gap-1 text-xs text-gray-500">
                                <Icon className="h-3.5 w-3.5 flex-shrink-0" />
                                {f.value}
                            </span>
                        );
                    })}
                </div>
            )}

            {/* Footer: price + CTA */}
            <div className="flex items-center justify-between pt-1 border-t border-gray-50">
                {prix ? (
                    <p className="text-sm text-gray-700">
                        {t("Starting from")} <span className="font-extrabold text-gray-900">{prix}</span>
                    </p>
                ) : <span />}
                <button
                    onClick={postulate}
                    className="flex items-center gap-1.5 text-sm font-semibold color-primary-perso group-hover:underline transition-all"
                >
                    {loading ? <LoadingSpinner /> : <>{t("Postuler")} →</>}
                </button>
            </div>
        </div>
    );
}

export default withTranslation()(NewFormCard);